/**
 * THE CHAT THREAD A CALL BELONGS TO.
 *
 * `cyphex.calls.conversation_id` starts as whatever the client attached when
 * the call was placed, and only ONE of the three ways to place a call attaches
 * anything: ringing somebody from a chat thread carries it; the dialler and
 * "call again" do not. So a large share of calls arrive here with no thread —
 * for people whose chat history is one click away in Chat.
 *
 * Hiding or disabling the chat button for those calls would make the affordance
 * blink in and out depending on which BUTTON started the call, for the same two
 * people, which is the sort of lie this feature exists to remove. So the thread
 * is RESOLVED instead: `POST /v1/conversations` is idempotent by
 * `uq_conversations_member_hash`, and the hash is order-independent over the
 * deduped participant set, so every party to the call converges on the same row
 * — creating one only if these people have never spoken.
 *
 * ...and then the resolution is WRITTEN BACK, which is what makes it a fact
 * rather than a coincidence. See the latch note below.
 *
 * Lazily, on first open, never at call time: placing a call is on the ring
 * latency path and must not wait on manufacturing a chat nobody may open.
 */
import { useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Conversation, ConversationId, UserId } from '@cyphex/types';
/* Deep import: the calls chunk must not pull the chat PAGES in through the
   feature barrel just to open one thread. */
import { chatApi } from '@/features/chat/api';
import { callsApi } from '../data/callsApi';
import { CALL_CHAT_KEYS } from '../data/queryKeys';

export interface CallConversation {
  /** Null only while resolving, or when resolving failed. */
  readonly conversationId: ConversationId | null;
  readonly isResolving: boolean;
  readonly isError: boolean;
  /** Try the resolution again, by hand. Stable across renders. */
  readonly retry: () => void;
}

/**
 * ONE CALL RESOLVES TO ONE THREAD, ONCE — AND EVERY PARTY GETS THE SAME ONE.
 *
 * Keyed by call id rather than by roster, and remembered outside React, because
 * the roster MOVES: `POST /calls/:id/participants` adds a leg, and a key
 * derived from the roster then changed mid-call. What that did was not
 * "re-resolve" — it silently SPLIT the conversation. The panel dropped to a
 * skeleton, a second thread was created for the larger member set, and the
 * messages either side of the split sat in two different threads with nothing
 * on screen saying so. The composer draft is keyed by conversation id
 * (`useComposerDraft`), so half-typed text was stranded in a thread the user
 * could no longer reach, along with their reply target and scroll position.
 *
 * Sticking is the correct answer, not merely the cheaper one: which thread a
 * call's chat is IS a fact, established when the chat opens. Adding a fourth
 * person to a call does not retroactively move what was already said, and the
 * person added reads it in Chat the way anyone added to a group does.
 *
 * THIS MAP ALONE WAS NOT ENOUGH, though, and that is the bug it hid. It is per
 * client and per page load, so convergence still depended on every party seeing
 * the same roster at the moment they happened to open the rail:
 *
 *   Alice opens the rail on a 1:1 with Bob   -> roster {bob}          -> DM
 *   Carol is added; Bob NOW opens his rail   -> roster {alice, carol} -> group
 *
 * ...two people typing into different threads on one call. So the first
 * resolution is also POSTed to `/calls/:id/conversation`, which accepts it only
 * while the call has none and answers with the winner. From then on every
 * client — including this one after a reload, when neither this map nor the
 * query cache survives — reads it straight off `GET /calls/:id` as `linkedId`
 * and resolves nothing at all.
 *
 * The map outlives the panel deliberately — walking away puts the surface in
 * compact mode, which UNMOUNTS the rail — and it is not React state because
 * nothing renders from it. One string per call placed in one page session.
 */
const RESOLVED_BY_CALL = new Map<string, ConversationId>();

/**
 * Persist the resolution and report whoever actually holds the call.
 *
 * Null means the write could not happen at all — offline, a 502, a call the
 * server no longer has. Deliberately not fatal: a call whose thread could not
 * be latched still chats exactly as it did before this endpoint existed, it
 * merely falls back to per-client convergence. Failing the resolve over it
 * would trade a rare split for a rail that does not open.
 */
async function latchOnServer(
  callId: string,
  conversationId: ConversationId,
): Promise<ConversationId | null> {
  try {
    const result = await callsApi.linkConversation(callId, conversationId);
    return result.conversationId;
  } catch {
    return null;
  }
}

/**
 * @param callId        The call this rail belongs to. The resolution latch.
 * @param linkedId      `call.conversationId`, or null when the call carries none.
 * @param rosterUserIds Everybody on the call except me, whatever state their leg
 *                      is in — read only when the resolve actually runs.
 * @param persist       May the resolution be written back to the call? False in
 *                      exactly one case: the call HAS a thread and the server
 *                      refused us it (`CallChatPanel`'s 403/404 fall-through).
 *                      Writing then would be wrong twice over — the conditional
 *                      UPDATE cannot land on a call that already has one, and
 *                      the id it would answer with is the very one we have just
 *                      been told we may not read.
 */
export function useCallConversation(
  callId: string,
  linkedId: ConversationId | null,
  rosterUserIds: readonly UserId[],
  persist: boolean,
): CallConversation {
  const queryClient = useQueryClient();
  /* Refs, not query keys: both are INPUTS to the one resolve, never a reason to
     run another. See the latch note above. */
  const roster = useRef(rosterUserIds);
  roster.current = rosterUserIds;
  const mayPersist = useRef(persist);
  mayPersist.current = persist;

  const needsResolve = linkedId === null && rosterUserIds.length > 0;

  const query = useQuery({
    queryKey: CALL_CHAT_KEYS.conversation(callId),
    queryFn: async (): Promise<Conversation> => {
      /*
       * A REFETCH MUST NOT BE A SECOND CREATE.
       *
       * `staleTime: Infinity` does not make a query unrefetchable, and two
       * places invalidate the ENTIRE cache — `applyToShell` on
       * `session.permissions_changed`, and `useTopicReset` on a refused resume,
       * which is what a laptop waking mid-call produces. Both match every key
       * there is. So once this call has a thread, re-running the queryFn
       * re-READS it: the write behind `rateLimit('send')` happens exactly once
       * per call, no matter how often anything asks.
       */
      const latched = RESOLVED_BY_CALL.get(callId);
      if (latched !== undefined) return chatApi.getConversation(latched);

      /* Sorted only so the request is deterministic; the server's hash dedupes
         and sorts anyway, so this is for logs and replay, not correctness. */
      const memberIds = [...roster.current].sort();
      const created = await chatApi.createConversation({
        /* A DM is exactly two people, so exactly one member besides me.
           Anything larger is a group thread — the API enforces the same rule. */
        kind: memberIds.length === 1 ? 'dm' : 'group',
        memberIds,
      });

      const winner = mayPersist.current ? await latchOnServer(callId, created.id) : null;
      if (winner === null || winner === created.id) {
        RESOLVED_BY_CALL.set(callId, created.id);
        return created;
      }

      /*
       * ANOTHER PARTY GOT THERE FIRST, and theirs is the thread every client
       * now reads off `GET /calls/:id`. Adopt what came BACK rather than what
       * went out — that is why the endpoint answers with the winner instead of
       * an ok. Latched before the read, so a retry after a failed fetch
       * re-reads the same row rather than resolving a second time.
       */
      RESOLVED_BY_CALL.set(callId, winner);
      return chatApi.getConversation(winner);
    },
    enabled: needsResolve,
    /* Idempotent by member hash and latched by call id, so re-asking could only
       re-read a row we are already holding. */
    staleTime: Number.POSITIVE_INFINITY,
    /* Survives the rail being closed and reopened, and the surface going
       compact and back — each of which unmounts this hook. Without it the entry
       is collected after five observerless minutes and the next open resolves
       again, against whatever the roster has become by then. */
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  /* Not `query.refetch`: its identity changes every render and this is a button
     handler. Refetching re-runs the queryFn, which is safe by construction —
     the create is idempotent by member hash, and once latched it is a read. */
  const retry = useCallback(() => {
    void queryClient.refetchQueries({ queryKey: CALL_CHAT_KEYS.conversation(callId) });
  }, [queryClient, callId]);

  if (linkedId !== null) {
    return { conversationId: linkedId, isResolving: false, isError: false, retry };
  }

  return {
    conversationId: query.data?.id ?? null,
    /* An empty roster is the few hundred ms before `GET /calls/:id` lands, not
       a call with nobody on it — a spinner, not a failure. A roster that never
       lands AT ALL is neither, and is reported separately by
       `useCallParties.isError`; `CallChatPanel` branches on that before it ever
       reaches this flag. */
    isResolving: rosterUserIds.length === 0 || (!query.isError && query.data === undefined),
    isError: query.isError,
    retry,
  };
}
